import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography
} from "@material-ui/core";
import {
	MoreVert,
	Replay,
	PauseCircleOutline,
	PlayArrow,
	CheckCircleOutline
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 8,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		padding: "4px 6px",
		minHeight: 40,
		borderRadius: 999,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(15, 23, 42, 0.34)"
				: "rgba(15, 23, 42, 0.03)",
		border: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.24)"
				: "rgba(148, 163, 184, 0.26)"
		}`,
	},
	softButton: {
		borderRadius: 999,
		padding: "5px 10px",
		minHeight: 32,
		fontWeight: 700,
		letterSpacing: 0,
		textTransform: "none",
		color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155",
		borderColor:
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.28)"
				: "rgba(148, 163, 184, 0.36)",
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(30, 41, 59, 0.38)"
				: "rgba(255, 255, 255, 0.78)",
		"&:hover": {
			backgroundColor:
				theme.palette.type === "dark"
					? "rgba(51, 65, 85, 0.45)"
					: "rgba(241, 245, 249, 0.95)"
		}
	},
	actionIconButton: {
		width: 32,
		height: 32,
		borderRadius: 999,
		color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155",
		border: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.28)"
				: "rgba(148, 163, 184, 0.36)"
		}`,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(30, 41, 59, 0.38)"
				: "rgba(255, 255, 255, 0.78)",
		"&:hover": {
			backgroundColor:
				theme.palette.type === "dark"
					? "rgba(51, 65, 85, 0.45)"
					: "rgba(241, 245, 249, 0.95)"
		}
	},
	actionIconButtonPrimary: {
		width: 32,
		height: 32,
		borderRadius: 999,
		color: "#ffffff",
		background:
			theme.palette.type === "dark"
				? "linear-gradient(180deg, #2aa092 0%, #176b63 100%)"
				: "linear-gradient(180deg, #22877d 0%, #176b63 100%)",
		border: "none",
		"&:hover": {
			background:
				theme.palette.type === "dark"
					? "linear-gradient(180deg, #34b5a5 0%, #1e7d72 100%)"
					: "linear-gradient(180deg, #299488 0%, #1b776d 100%)"
		}
	},
	resolveButton: {
		borderRadius: 999,
		padding: "5px 12px",
		minHeight: 32,
		fontWeight: 700,
		letterSpacing: 0,
		textTransform: "none",
		boxShadow: "none",
		"&:hover": {
			boxShadow: "none"
		}
	},
	menuButton: {
		marginLeft: 2,
		color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b"
	},
}));

const TicketActionButtons = ({ ticket, onRefreshTicket, readOnly = false }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [slaLoading, setSlaLoading] = useState(false);
	const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
	const [pauseSlaDialogOpen, setPauseSlaDialogOpen] = useState(false);
	const [slaPauseReason, setSlaPauseReason] = useState(
		"Aguardando retorno do cliente"
	);
	const [resolutionSubject, setResolutionSubject] = useState(ticket?.subject || "");
	const [resolutionSummary, setResolutionSummary] = useState(
		ticket?.resolutionSummary || ""
	);
	const [subjectOptions, setSubjectOptions] = useState([]);
	const [loadingSubjectOptions, setLoadingSubjectOptions] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	React.useEffect(() => {
		setResolutionSubject(ticket?.subject || "");
	}, [ticket?.subject, ticket?.id]);

	React.useEffect(() => {
		setResolutionSummary(ticket?.resolutionSummary || "");
	}, [ticket?.resolutionSummary, ticket?.id]);

	React.useEffect(() => {
		let isMounted = true;

		const loadSubjectOptions = async () => {
			if (!resolveDialogOpen || !ticket?.subjectRequired) {
				return;
			}

			setLoadingSubjectOptions(true);
			try {
				const { data } = await api.get("/ticket-subjects/options");
				if (!isMounted) return;
				setSubjectOptions(Array.isArray(data) ? data : []);
			} catch (err) {
				if (isMounted) {
					setSubjectOptions([]);
				}
				toastError(err);
			} finally {
				if (isMounted) {
					setLoadingSubjectOptions(false);
				}
			}
		};

		loadSubjectOptions();

		return () => {
			isMounted = false;
		};
	}, [resolveDialogOpen, ticket?.subjectRequired]);

	React.useEffect(() => {
		if (ticket?.slaPausedReason) {
			setSlaPauseReason(ticket.slaPausedReason);
			return;
		}
		setSlaPauseReason("Aguardando retorno do cliente");
	}, [ticket?.slaPausedReason, ticket?.id]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (
		e,
		status,
		userId,
		extraData = {}
	) => {
		setLoading(true);
		try {
			const payload = {
				status,
				...extraData
			};

			if (typeof userId !== "undefined") {
				payload.userId = userId;
			}

			const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, payload);
			window.dispatchEvent(
				new CustomEvent("ticket:updated", {
					detail: { ticket: updatedTicket }
				})
			);

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleOpenResolveDialog = () => {
		setResolutionSubject(ticket?.subject || "");
		setResolutionSummary(ticket?.resolutionSummary || "");
		setResolveDialogOpen(true);
	};

	const handleCloseResolveDialog = () => {
		if (loading) {
			return;
		}
		setResolveDialogOpen(false);
	};

	const handleResolveTicket = e =>
		handleUpdateTicketStatus(e, "closed", user?.id, {
			subject: resolutionSubject,
			resolutionSummary
		});

	const isAllowedResolutionSubject = !ticket?.subjectRequired
		? true
		: subjectOptions.length === 0
		? false
		: subjectOptions.some(
				item =>
					String(item?.name || "").trim().toLowerCase() ===
					String(resolutionSubject || "").trim().toLowerCase()
		  );

	const handleOpenPauseSlaDialog = () => {
		setPauseSlaDialogOpen(true);
	};

	const handleClosePauseSlaDialog = () => {
		if (slaLoading) {
			return;
		}
		setPauseSlaDialogOpen(false);
	};

	const handleUpdateSla = async action => {
		setSlaLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}/sla`, {
				action,
				...(action === "pause" ? { reason: slaPauseReason } : {})
			});
			setPauseSlaDialogOpen(false);
			if (typeof onRefreshTicket === "function") {
				onRefreshTicket();
			}
		} catch (err) {
			toastError(err);
		} finally {
			setSlaLoading(false);
		}
	};

	const isSlaPaused = Boolean(ticket?.slaPausedAt);

	if (readOnly) {
		return null;
	}

	return (
		<div className={classes.actionButtons}>
			<Dialog
				open={resolveDialogOpen}
				onClose={handleCloseResolveDialog}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Resolver chamado</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Informe o assunto para encerrar este chamado. O resumo é opcional.
					</Typography>
					{ticket?.subjectRequired ? (
						<FormControl fullWidth variant="outlined" margin="dense">
							<InputLabel id="resolve-ticket-subject-label">
								Assunto do chamado
							</InputLabel>
							<Select
								labelId="resolve-ticket-subject-label"
								label="Assunto do chamado"
								value={resolutionSubject}
								onChange={e => setResolutionSubject(e.target.value)}
								disabled={loadingSubjectOptions}
							>
								<MenuItem value="">
									<em>
										{loadingSubjectOptions
											? "Carregando assuntos..."
											: "Selecione o assunto"}
									</em>
								</MenuItem>
								{subjectOptions.map(item => (
									<MenuItem key={item.id} value={item.name}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : (
						<TextField
							fullWidth
							variant="outlined"
							label="Assunto do chamado"
							value={resolutionSubject}
							onChange={e => setResolutionSubject(e.target.value)}
							margin="dense"
						/>
					)}
					{ticket?.subjectRequired && subjectOptions.length === 0 && !loadingSubjectOptions ? (
						<Typography variant="body2" color="error" gutterBottom>
							Nenhum assunto cadastrado. Cadastre em "Assuntos do Chamado" para encerrar tickets.
						</Typography>
					) : null}
					<TextField
						fullWidth
						variant="outlined"
						label="Breve resumo"
						value={resolutionSummary}
						onChange={e => setResolutionSummary(e.target.value)}
						margin="dense"
						multiline
						minRows={4}
					/>
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={handleCloseResolveDialog}
						color="default"
					>
						Cancelar
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						onClick={handleResolveTicket}
						color="primary"
						variant="contained"
						disabled={
							loadingSubjectOptions ||
							(ticket?.subjectRequired && !isAllowedResolutionSubject)
						}
					>
						Resolver
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			<Dialog
				open={pauseSlaDialogOpen}
				onClose={handleClosePauseSlaDialog}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Pausar SLA</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Informe o motivo da pausa para auditoria de atendimento.
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						label="Motivo da pausa"
						value={slaPauseReason}
						onChange={e => setSlaPauseReason(e.target.value)}
						margin="dense"
						multiline
						minRows={3}
					/>
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={handleClosePauseSlaDialog}
						color="default"
					>
						Cancelar
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={slaLoading}
						onClick={() => handleUpdateSla("pause")}
						color="primary"
						variant="contained"
					>
						Pausar SLA
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading || slaLoading}
					startIcon={<Replay />}
					size="small"
					variant="outlined"
					className={classes.softButton}
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					{isSlaPaused ? (
						<Tooltip arrow title="Retomar SLA">
							<span>
								<IconButton
									size="small"
									className={classes.actionIconButton}
									onClick={() => handleUpdateSla("resume")}
									disabled={loading || slaLoading}
								>
									<PlayArrow fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					) : (
						<Tooltip arrow title="Pausar SLA">
							<span>
								<IconButton
									size="small"
									className={classes.actionIconButton}
									onClick={handleOpenPauseSlaDialog}
									disabled={loading || slaLoading}
								>
									<PauseCircleOutline fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					<Tooltip arrow title={i18n.t("messagesList.header.buttons.return")}>
						<span>
							<IconButton
								size="small"
								className={classes.actionIconButton}
								onClick={e => handleUpdateTicketStatus(e, "pending", null)}
								disabled={loading || slaLoading}
							>
								<Replay fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip arrow title={i18n.t("messagesList.header.buttons.resolve")}>
						<span>
							<IconButton
								size="small"
								className={classes.actionIconButtonPrimary}
								onClick={e =>
									ticket.subjectRequired
										? handleOpenResolveDialog()
										: handleUpdateTicketStatus(e, "closed", user?.id)
								}
								disabled={loading || slaLoading}
							>
								<CheckCircleOutline fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<IconButton
						size="small"
						className={classes.menuButton}
						onClick={handleOpenTicketOptionsMenu}
					>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						onRefreshTicket={onRefreshTicket}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading || slaLoading}
					size="small"
					variant="contained"
					color="primary"
					className={classes.resolveButton}
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
		</div>
	);
};

export default TicketActionButtons;
